// @oasis-permission-inherits: /jerboa-client-export (jerboa:view)
import { Link, createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

import { Badge } from "~/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/utils";
import {
  formatBytes,
  formatClientFailurePhase,
  formatDurationMs,
  formatErrorCode,
  formatExportMethod,
  formatFps,
  formatFrameMs,
  formatMb,
  formatServerExportStatus,
  formatYesNo,
  frameRenderQualityFromP90,
  qualityBadgeClass,
  serverExportStatusVariant,
  statusVariant,
} from "~/modules/jerboa-client-export/formatters";
import { LifecycleTimeline } from "~/modules/jerboa-client-export/lifecycle-timeline";
import { ProgressChart } from "~/modules/jerboa-client-export/progress-chart";
import { getJerboaClientExportQuery } from "~/modules/jerboa-client-export/queries";
import { jerboaClientExportStatusSchema } from "~/modules/jerboa-client-export/schemas";
import { JerboaUserEnvCard } from "~/modules/jerboa/components/user-env-card";

export const Route = createFileRoute("/_dash/jerboa-client-export/$id")({
  validateSearch: z.object({
    page: z.number().optional(),
    status: jerboaClientExportStatusSchema.optional(),
    projectId: z.string().optional(),
  }),
  component: RouteComponent,
  async loader({ params, context }) {
    const { queryClient } = context;
    await queryClient.prefetchQuery(
      getJerboaClientExportQuery.queryOptions({
        pathParams: { id: params.id },
      })
    );
  },
  pendingComponent: PendingComponent,
});

function PendingComponent() {
  const { page, status, projectId } = Route.useSearch();
  return (
    <div className="space-y-6">
      <Link
        to="/jerboa-client-export"
        search={{ page, status, projectId }}
        className="text-sm text-muted-foreground hover:underline"
      >
        &larr; Back to Jerboa Client Exports
      </Link>
      <Skeleton className="h-10 w-96 rounded-md" />
      <Skeleton className="h-48 rounded-md" />
      <Skeleton className="h-48 rounded-md" />
    </div>
  );
}

function MetricCard({
  label,
  value,
  className,
}: {
  label: string;
  value: string | number | null | undefined;
  className?: string;
}) {
  return (
    <div className={cn("rounded-lg border p-3", className)}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm font-medium">{value ?? "-"}</div>
    </div>
  );
}

function RouteComponent() {
  const { id } = Route.useParams();
  const { page, status, projectId } = Route.useSearch();

  const { data } = getJerboaClientExportQuery.useSuspenseQuery({
    pathParams: { id },
  });

  const result = data.result;
  const errorCodeLabel = formatErrorCode(result.errorCode);
  // `targetFps ?? result.fps` — see the matching comment in
  // `index.tsx`; older exports and native runs ship `targetFps=null`
  // and would otherwise drop through to ~55fps-tuned hardcoded
  // thresholds.
  const frameP90Quality = frameRenderQualityFromP90(
    result.p90FrameRenderMs,
    result.targetFps ?? result.fps
  );

  return (
    <div className="space-y-6">
      <Link
        to="/jerboa-client-export"
        search={{ page, status, projectId }}
        className="text-sm text-muted-foreground hover:underline"
      >
        &larr; Back to Jerboa Client Exports
      </Link>

      <JerboaUserEnvCard
        id={result.id}
        captionsProjectId={result.captionsProjectId}
        projectProtoUri={result.protoDataUrl}
        jobId={result.jobId}
        userId={result.userId}
        orgId={result.orgId}
        browserInfo={result.browserInfo}
      />

      <Card>
        <CardHeader>
          <CardTitle>Overview</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground">Status:</span>
            <Badge variant={statusVariant(result.status)}>{result.status}</Badge>
            {errorCodeLabel && (
              <Badge variant="outline" className="text-xs">
                {errorCodeLabel}
              </Badge>
            )}
            {result.exportMethod && (
              <Badge variant="outline" className="text-xs">
                {formatExportMethod(result.exportMethod)}
              </Badge>
            )}
          </div>
          {result.editType && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Edit Type:</span>
              <Badge variant="outline">{result.editType}</Badge>
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Created At:</span>
            <span className="text-sm">{new Date(result.createdAt).toLocaleString()}</span>
          </div>
          {result.errorMessage && (
            <div>
              <span className="text-sm text-muted-foreground">Error:</span>
              <pre className="mt-1 overflow-auto whitespace-pre-wrap break-words rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {result.errorMessage}
              </pre>
            </div>
          )}
          {result.errorCategory && !errorCodeLabel && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Error Category:</span>
              <Badge variant="outline">{result.errorCategory}</Badge>
            </div>
          )}
        </CardContent>
      </Card>

      {result.status === "error" &&
        (result.operationId ||
          result.serverExportStatus ||
          result.serverExportError ||
          result.clientFailureErrorCode) && (
          <Card>
            <CardHeader>
              <CardTitle>Server Fallback</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                {result.serverExportStatus && (
                  <>
                    <span className="text-sm text-muted-foreground">Fallback status:</span>
                    <Badge variant={serverExportStatusVariant(result.serverExportStatus)}>
                      {formatServerExportStatus(result.serverExportStatus)}
                    </Badge>
                  </>
                )}
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <MetricCard
                  label="Operation ID"
                  value={result.operationId ?? "-"}
                  className="font-mono text-xs"
                />
                <MetricCard
                  label="Download Attempt ID"
                  value={result.downloadAttemptId ?? "-"}
                  className="font-mono text-xs"
                />
              </div>
              {result.serverExportError && (
                <div>
                  <span className="text-sm text-muted-foreground">Server error:</span>
                  <pre className="mt-1 overflow-auto whitespace-pre-wrap break-words rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                    {result.serverExportError}
                  </pre>
                </div>
              )}
              {(result.clientFailureErrorCode ||
                result.clientFailurePhase ||
                result.clientFailureErrorCategory ||
                result.clientFailureErrorMessage) && (
                <div className="space-y-3 rounded-md border bg-muted/20 p-3">
                  <div className="text-sm font-medium">
                    Client export failure that triggered fallback
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {result.clientFailureErrorCode && (
                      <Badge variant="outline" className="text-xs">
                        {formatErrorCode(result.clientFailureErrorCode) ??
                          result.clientFailureErrorCode}
                      </Badge>
                    )}
                    {result.clientFailurePhase && (
                      <Badge variant="outline" className="text-xs">
                        Phase: {formatClientFailurePhase(result.clientFailurePhase)}
                      </Badge>
                    )}
                    {result.clientFailureErrorCategory && (
                      <Badge variant="outline" className="text-xs">
                        Category: {result.clientFailureErrorCategory}
                      </Badge>
                    )}
                  </div>
                  {result.clientFailureErrorMessage && (
                    <pre className="overflow-auto whitespace-pre-wrap break-words rounded-md bg-background p-3 text-xs">
                      {result.clientFailureErrorMessage}
                    </pre>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

      <Card>
        <CardHeader>
          <CardTitle>Export Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            <MetricCard label="Resolution" value={result.resolution.toUpperCase()} />
            <MetricCard label="Width" value={result.width} />
            <MetricCard label="Height" value={result.height} />
            <MetricCard label="FPS" value={result.fps} />
            <MetricCard label="Codec" value={result.codec ?? "-"} />
            <MetricCard label="Auto Download" value={formatYesNo(result.autoDownload)} />
            <MetricCard label="Concurrent Exports" value={result.concurrentExportCount ?? "-"} />
            <MetricCard label="Total Frames" value={result.totalFrames ?? "-"} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            <MetricCard label="Duration" value={formatDurationMs(result.durationMs)} />
            <MetricCard label="Output Size" value={formatBytes(result.outputSizeBytes)} />
            <MetricCard label="WASM Load" value={formatDurationMs(result.wasmLoadDurationMs)} />
            <MetricCard label="WASM Init" value={formatDurationMs(result.wasmInitDurationMs)} />
          </div>
        </CardContent>
      </Card>

      {(result.lifecycleSpans?.length ?? 0) > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Lifecycle Timeline</CardTitle>
          </CardHeader>
          <CardContent>
            <LifecycleTimeline
              spans={result.lifecycleSpans ?? []}
              totalDurationMs={result.durationMs}
            />
          </CardContent>
        </Card>
      )}

      {(result.progressTMs?.length ?? 0) > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <ProgressChart
              tMs={result.progressTMs ?? []}
              frameIndices={result.progressFrameIndices ?? []}
              encoderQueueDepths={result.progressEncoderQueueDepths}
              totalFrames={result.totalFrames}
            />
          </CardContent>
        </Card>
      )}

      {(result.framesRendered != null ||
        result.avgPlaybackFps != null ||
        result.p90FrameRenderMs != null) && (
        <Card>
          <CardHeader>
            <CardTitle>Playback Performance</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              <MetricCard label="Frames Rendered" value={result.framesRendered ?? "-"} />
              <MetricCard label="Target FPS" value={result.targetFps ?? "-"} />
              <MetricCard label="Avg FPS" value={formatFps(result.avgPlaybackFps)} />
              <MetricCard label="P50 FPS" value={formatFps(result.p50PlaybackFps)} />
              <MetricCard label="P90 FPS" value={formatFps(result.p90PlaybackFps)} />
              <MetricCard label="P99 FPS" value={formatFps(result.p99PlaybackFps)} />
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              <MetricCard label="Avg Frame Render" value={formatFrameMs(result.avgFrameRenderMs)} />
              <MetricCard label="P50 Frame Render" value={formatFrameMs(result.p50FrameRenderMs)} />
              <MetricCard
                label="P90 Frame Render"
                value={formatFrameMs(result.p90FrameRenderMs)}
                className={cn(
                  result.p90FrameRenderMs != null && qualityBadgeClass(frameP90Quality)
                )}
              />
              <MetricCard label="P99 Frame Render" value={formatFrameMs(result.p99FrameRenderMs)} />
              <MetricCard label="Max Frame Render" value={formatFrameMs(result.maxFrameRenderMs)} />
              <MetricCard
                label="Longest Frame Gap"
                value={formatFrameMs(result.longestFrameGapMs)}
              />
              <MetricCard label="Slow Frames" value={result.slowFrameCount ?? "-"} />
              <MetricCard label="Decoder Errors" value={result.decoderErrorCount ?? "-"} />
            </div>
          </CardContent>
        </Card>
      )}

      {(result.peakWasmMemoryMb != null ||
        result.peakWasmLiveMb != null ||
        result.peakJsHeapMb != null) && (
        <Card>
          <CardHeader>
            <CardTitle>Memory</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              <MetricCard label="Peak WASM Reserved" value={formatMb(result.peakWasmMemoryMb)} />
              <MetricCard label="Avg WASM Reserved" value={formatMb(result.avgWasmMemoryMb)} />
              <MetricCard label="Peak WASM Live" value={formatMb(result.peakWasmLiveMb)} />
              <MetricCard label="Avg WASM Live" value={formatMb(result.avgWasmLiveMb)} />
              <MetricCard label="Peak JS Heap" value={formatMb(result.peakJsHeapMb)} />
              <MetricCard label="Avg JS Heap" value={formatMb(result.avgJsHeapMb)} />
            </div>
            {result.peakWasmLiveMb == null && (
              <p className="text-xs text-muted-foreground">
                WASM live bytes not available — captured by ENG-1082&apos;s `TrackingAllocator` wrap
                on web exports only. Native exports surface RSS via `peakWasmReservedMb` instead.
              </p>
            )}
            {result.peakJsHeapMb == null && (
              <p className="text-xs text-muted-foreground">
                JS heap not available — `performance.memory` is Chromium-only.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {result.assetUrlMap && Object.keys(result.assetUrlMap).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Asset URL Map</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="max-h-64 overflow-auto rounded-md bg-muted p-3 text-xs">
              {JSON.stringify(result.assetUrlMap, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Analytics Events ({result.events.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {result.events.length === 0 ? (
            <div className="text-sm text-muted-foreground">No events captured</div>
          ) : (
            <div className="space-y-3">
              {result.events.map((event, i) => (
                <div key={i} className="rounded-md border p-3">
                  <div className="mb-2 flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {event.source}
                    </Badge>
                    <code className="text-sm">{event.name}</code>
                    <span className="text-xs text-muted-foreground">
                      {new Date(event.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  <pre className="max-h-64 overflow-auto rounded-md bg-muted p-2 text-xs">
                    {JSON.stringify(event.properties, null, 2)}
                  </pre>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
